import { getToken, parseJwt } from '../auth';

/**
 * Get the RiseAI API base URL
 * In development (localhost), uses relative paths which are proxied by setupProxy.js to avoid CORS
 * Set REACT_APP_RISEAI_HOST to override the host/base URL (defaults to https://riseai-dev.netrise.io)
 */
const getRiseAIBaseUrl = () => {
  // Get the host/base URL (defaults to https://riseai-dev.netrise.io)
  // Remove trailing slash if present to avoid double slashes
  const defaultHost = 'https://riseai-dev.netrise.io';
  const host = process.env.REACT_APP_RISEAI_HOST || defaultHost;
  const normalizedHost = host.endsWith('/') ? host.slice(0, -1) : host;

  // In development (localhost), use relative path which will be proxied by setupProxy.js
  // This avoids CORS issues by making requests go through the dev server proxy
  if (process.env.NODE_ENV === 'development' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    // Return empty string to use relative paths - the proxy will handle routing to the correct host
    return '';
  }

  // In production, use direct URL
  return normalizedHost;
};

/**
 * Extract username from JWT token
 * Handles URL-safe base64 decoding similar to the Postman pre-request script
 * @param {string} token - JWT token
 * @returns {string|null} - Username (subject) from token, or null if not found
 */
export const getUsernameFromToken = token => {
  if (!token) {
    return null;
  }

  try {
    // Split the JWT into its three parts
    const parts = token.split('.');

    if (parts.length !== 3) {
      console.error('Invalid JWT format');
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];

    // Base64 decode - handle URL-safe base64 (same as Postman script)
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

    // Add padding if needed
    const paddedBase64 = base64 + '='.repeat((4 - (base64.length % 4)) % 4);

    // Decode base64
    const jsonPayload = decodeURIComponent(
      atob(paddedBase64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    const decoded = JSON.parse(jsonPayload);

    // Extract the subject (username) - 'sub' is the standard JWT field for subject
    const username = decoded?.sub || decoded?.username || null;

    return username;
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    // Fallback to using the existing parseJwt function
    try {
      const decoded = parseJwt(token);
      return decoded?.sub || decoded?.username || null;
    } catch (fallbackError) {
      console.error('Fallback JWT parsing also failed:', fallbackError);
      return null;
    }
  }
};

/**
 * Get or create a session ID for RiseAI
 * @param {Object} options - Configuration options
 * @param {string} options.appName - RiseAI app name (default: 'rise_ai_llm_agent')
 * @param {string} options.assetId - Asset ID to associate with the session
 * @param {string} [options.token] - JWT token (if not provided, will be retrieved from localStorage)
 *                                    For best results, use getAccessTokenSilently() from useAuth0() hook
 *                                    and pass the token here
 * @returns {Promise<Object>} - Session data including sessionId
 * @throws {Error} - If the request fails or required parameters are missing
 */
export const getSessionId = async ({ appName = 'rise_ai_llm_agent', assetId, token = null }) => {
  if (!assetId) {
    throw new Error('Asset ID is required to get session ID');
  }

  // Get token - prefer provided token, fallback to localStorage
  // Note: For best results, components should use getAccessTokenSilently() from useAuth0()
  // and pass the token here, as it provides a fresh token with proper audience
  const jwtToken = token || getToken();
  if (!jwtToken) {
    throw new Error('JWT token is required. Please ensure you are authenticated.');
  }

  // Extract username from JWT token
  const username = getUsernameFromToken(jwtToken);
  if (!username) {
    throw new Error('Unable to extract username from JWT token. Token may be invalid.');
  }

  // Build the API URL
  const baseUrl = getRiseAIBaseUrl();
  // Ensure proper URL construction
  // If baseUrl is empty (development with proxy), start with /apps
  // Otherwise, ensure we have the full URL
  const url = baseUrl ? `${baseUrl}/apps/${appName}/users/${username}/sessions` : `/apps/${appName}/users/${username}/sessions`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Authorization': `Bearer ${jwtToken}`,
        'X-Asset-Id': assetId
      }
    });

    // Check content type before parsing
    const contentType = response.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      // If we got HTML (like a 404 page), provide a more helpful error
      if (!isJson && errorText.includes('<!DOCTYPE')) {
        throw new Error(`Failed to get session ID: ${response.status} - Server returned HTML (likely 404). URL: ${url}`);
      }
      throw new Error(`Failed to get session ID: ${response.status}${errorText ? ` - ${errorText}` : ''}`);
    }

    // Only try to parse as JSON if content type indicates JSON
    if (!isJson) {
      const text = await response.text();
      console.error('Expected JSON but got:', contentType, text.substring(0, 200));
      throw new Error(`Server returned non-JSON response. Content-Type: ${contentType}. URL: ${url}`);
    }

    const sessionData = await response.json();
    return sessionData;
  } catch (error) {
    console.error('Error getting session ID:', error);
    console.error('Request URL:', url);
    throw error;
  }
};

/**
 * Create a new session for RiseAI
 * @param {Object} options - Configuration options
 * @param {string} options.appName - RiseAI app name (default: 'rise_ai_llm_agent')
 * @param {string} options.assetId - Asset ID to associate with the session
 * @param {string} [options.token] - JWT token (if not provided, will be retrieved from localStorage)
 * @param {Object} [options.sessionData] - Additional session data to send
 * @returns {Promise<Object>} - Created session data including sessionId
 * @throws {Error} - If the request fails or required parameters are missing
 */
export const createSession = async ({ appName = 'rise_ai_llm_agent', assetId, token = null, sessionData = {} }) => {
  if (!assetId) {
    throw new Error('Asset ID is required to create session');
  }

  // Get token from localStorage if not provided
  const jwtToken = token || getToken();
  if (!jwtToken) {
    throw new Error('JWT token is required. Please ensure you are authenticated.');
  }

  // Extract username from JWT token
  const username = getUsernameFromToken(jwtToken);
  if (!username) {
    throw new Error('Unable to extract username from JWT token. Token may be invalid.');
  }

  // Build the API URL
  const baseUrl = getRiseAIBaseUrl();
  // Ensure proper URL construction
  // If baseUrl is empty (development with proxy), start with /apps
  // Otherwise, ensure we have the full URL
  const url = baseUrl ? `${baseUrl}/apps/${appName}/users/${username}/sessions` : `/apps/${appName}/users/${username}/sessions`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Authorization': `Bearer ${jwtToken}`,
        'X-Asset-Id': assetId
      },
      body: JSON.stringify(sessionData)
    });

    // Check content type before parsing
    const contentType = response.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      // If we got HTML (like a 404 page), provide a more helpful error
      if (!isJson && errorText.includes('<!DOCTYPE')) {
        throw new Error(`Failed to create session: ${response.status} - Server returned HTML (likely 404). URL: ${url}`);
      }
      throw new Error(`Failed to create session: ${response.status}${errorText ? ` - ${errorText}` : ''}`);
    }

    // Only try to parse as JSON if content type indicates JSON
    if (!isJson) {
      const text = await response.text();
      console.error('Expected JSON but got:', contentType, text.substring(0, 200));
      throw new Error(`Server returned non-JSON response. Content-Type: ${contentType}. URL: ${url}`);
    }

    const sessionDataResponse = await response.json();
    return sessionDataResponse;
  } catch (error) {
    console.error('Error creating session:', error);
    console.error('Request URL:', url);
    throw error;
  }
};
